/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

/**********ORIGINAL ALFRESCO DEFAULT ROUTE **************/
// import { AppLoginComponent } from './components/login/app-login.component';

// export const APP_ROUTES = [
//   {
//     path: 'login',
//     component: AppLoginComponent,
//     data: {
//       title: 'APP.SIGN_IN'
//     }
//   }
// ];

/**********CUSTOM LOGIN ROUTE **************/

// To this:
import { SkyPlusLoginComponent } from './components/skyplus/login/skyplus-login.component';
import { SkyPlusDashboardComponent } from './components/skyplus/dashboard/skyplus-dashboard.component';
import { WfMyWorkComponent } from './components/skyplus/wf-runtime/pages/wf-my-work.component';
import { WfMyCompletedComponent } from './components/skyplus/wf-runtime/pages/wf-my-completed.component';
import { SkyPlusDocumentLibraryComponent } from './components/skyplus/document-library/skyplus-document-library.component';
import { SkyPlusProfileComponent } from './components/skyplus/profile/skyplus-profile.component';
import { IndexingQueueComponent } from './components/skyplus/indexing/indexing-queue.component';
import { SkyPlusSearchComponent } from './components/skyplus/search/skyplus-search.component';
import { SkyPlusReportsComponent } from './components/skyplus/reports/skyplus-reports.component';
import { SkyPlusWorkflowComponent } from './components/skyplus/workflow/skyplus-workflow.component';
import { SkyPlusWarehouseComponent } from './components/skyplus/warehouse/skyplus-warehouse.component';
import { CaseDashboardComponent } from './components/skyplus/case-management/dashboard/case-dashboard.component';
import { CaseListComponent }      from './components/skyplus/case-management/list/case-list.component';
import { CaseDetailComponent }    from './components/skyplus/case-management/detail/case-detail.component';
import { CaseNewComponent }       from './components/skyplus/case-management/wizard/case-new.component';
import { SkyPlusContractsComponent } from './components/skyplus/contracts/skyplus-contracts.component';
import { SkyPlusContractWizardComponent } from './components/skyplus/contract-wizard/skyplus-contract-wizard.component';
import { ContractDetailsComponent } from './components/skyplus/contracts/contract-details.component';
import { ContractWorkflowDetailComponent } from './components/skyplus/contract-workflow/contract-workflow-detail.component';
import { ExecutedContractUploadComponent } from './components/skyplus/contracts/executed-contract-upload.component';
import { ContractWorkflowPageComponent } from './components/skyplus/contract-workflow/contract-workflow-page.component';
import { MailWorkflowPageComponent }   from './components/skyplus/mail-workflow/mail-workflow-page.component';
import { MailWorkflowDetailComponent } from './components/skyplus/mail-workflow/mail-workflow-detail.component';
import { MailInitiateComponent }         from './components/skyplus/mail-workflow/mail-initiate.component';
import { MailMemoTemplateComponent }     from './components/skyplus/mail-workflow/mail-memo-template.component';
import { MenuGuard } from './components/skyplus/shared/guards/menu.guard';
import { FeatureGuard } from './components/skyplus/shared/sector/feature.guard';
import { FlipViewerComponent } from './components/skyplus/flip-viewer/flip-viewer.component';
import { IntelligenceComponent } from './components/skyplus/intelligence/intelligence.component';
import { SkyplusLicenseComponent } from './components/skyplus/license/skyplus-license.component';
import { MenuManagerComponent } from './components/skyplus/admin/menu-manager/menu-manager.component';
import { wfRoleGuard } from './components/skyplus/wf-admin/guards/wf-role.guard';

import { AuthGuard } from '@alfresco/adf-core';

// And change the component in the route:
export const APP_ROUTES = [
{
  path: 'login',
  component: SkyPlusLoginComponent,  // Changed
  data: {
    title: 'APP.SIGN_IN'
  }
},
  {
    path: 'dashboard',
    component: SkyPlusDashboardComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Dashboard'
    }
  },
  {
    // License page — always accessible (AuthGuard only, no MenuGuard) so the
    // "License" entry can never be locked out; the nav entry is seeded CORE.
    path: 'license',
    component: SkyplusLicenseComponent,
    canActivate: [AuthGuard],
    data: { title: 'License - SkyPlus' }
  },
  {
    // Menu Manager — SKYPLUS_ADMIN only (route-guarded AND backend-gated).
    path: 'admin/menu-manager',
    component: MenuManagerComponent,
    canActivate: [AuthGuard, wfRoleGuard],
    data: { title: 'Menu Manager - SkyPlus', requiredRoles: ['SKYPLUS_ADMIN', 'ADMIN'] }
  },
  {
    // Unified cross-workflow inbox — its OWN top-level page (no workflow shell
    // / sidebar). AuthGuard only: MenuGuard checks the first URL segment and
    // would bounce a non-menu path to /dashboard, so it is intentionally omitted.
    path: 'my-work',
    component: WfMyWorkComponent,
    canActivate: [AuthGuard],
    data: { title: 'My Work - SkyPlus' }
  },
  {
    // "Completed" drill-down from the dashboard — the caller's completed tasks (own page, no shell).
    path: 'my-completed',
    component: WfMyCompletedComponent,
    canActivate: [AuthGuard],
    data: { title: 'Completed Tasks - SkyPlus' }
  },
  {
    path: 'documents',
    component: SkyPlusDocumentLibraryComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Document Library - SkyPlus',
      viewType: 'library'
    }
  },
  {
    path: 'documents/:folderId',
    component: SkyPlusDocumentLibraryComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Document Library - SkyPlus',
      viewType: 'library'
    }
  },
  {
    path: 'recent-files',
    component: SkyPlusDocumentLibraryComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Recent Documents - SkyPlus',
      viewType: 'recent'
    }
  },
  {
    path: 'favorites',
    component: SkyPlusDocumentLibraryComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'My Favourites - SkyPlus',
      viewType: 'favorites'
    }
  },
  {
    path: 'profile',
    component: SkyPlusProfileComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'My Profile - SkyPlus'
    }
  },
  {
  path: 'indexing',
  component: IndexingQueueComponent
  },
  {
    path: 'indexing/:nodeId',  // For edit mode
    component: IndexingQueueComponent
  },
  {
    path: 'search',
    component: SkyPlusSearchComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Search - SkyPlus'
    }
  },
  {
    path: 'reports',
    component: SkyPlusReportsComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Reports - SkyPlus'
    }
  },
  {
    path: 'workflow',
    component: SkyPlusWorkflowComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'Workflow - SkyPlus' }
  },
   // ==================== WAREHOUSE (NEW) ====================
  {
    path: 'warehouse',
    component: SkyPlusWarehouseComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Warehouse - SkyPlus'
    }
  },

  // ==================== CASE MANAGEMENT (NEW) ====================
  // Note: more specific routes BEFORE the parent so 'new' & 'cases' don't get
  // swallowed by the parent route's child matcher.
  {
    path: 'case-management/new',
    component: CaseNewComponent,
    canActivate: [AuthGuard, MenuGuard],
    data: { title: 'New Case - SkyPlus' }
  },
  {
    path: 'case-management/cases/:id',
    component: CaseDetailComponent,
    canActivate: [AuthGuard, MenuGuard],
    data: { title: 'Case Detail - SkyPlus' }
  },
  {
    path: 'case-management/cases',
    component: CaseListComponent,
    canActivate: [AuthGuard, MenuGuard],
    data: { title: 'All Cases - SkyPlus' }
  },
  {
    path: 'case-management',
    component: CaseDashboardComponent,
    canActivate: [AuthGuard, MenuGuard],
    data: { title: 'Case Management - SkyPlus' }
  },

  // ==================== CONTRACTS (NEW) ====================
  {
    path: 'contracts',
    component: SkyPlusContractsComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Contract Management - SkyPlus'
    }
  },
  {
    path: 'contract-wizard',
    component: SkyPlusContractWizardComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'New Contract - SkyPlus'
    }
  },
  {
    path: 'contract-wizard/:nodeId',
    component: SkyPlusContractWizardComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Edit Contract - SkyPlus'
    }
  },
   {
    path: 'contracts/upload-executed',
    component: ExecutedContractUploadComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Upload Executed Contract - SkyPlus'
    }
  },
  {
    path: 'contract-details/:nodeId',
    component: ContractDetailsComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: {
      title: 'Contract Details - SkyPlus'
    }
  },
  {
    path: 'workflow/contract-task/:taskId',
    component: ContractWorkflowDetailComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'Contract Task - SkyPlus' }
  },
  {
    path: 'contract-workflow',
    component: ContractWorkflowPageComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'Contract Workflow - SkyPlus' }
  },
 // Mail workflow shell (sidebar list view)
  {
    path: 'mail-workflow',
    component: MailWorkflowPageComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'Mail Management - SkyPlus' }
  },

  // Task detail (existing — dispatch & inbox tasks)
  {
    path: 'mail-workflow/task/:taskId',
    component: MailWorkflowDetailComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'Mail Task - SkyPlus' }
  },

  // ─── NEW in Step 7 ───────────────────────────────────────────────────────

  // Screen 6: New Mail Wizard (soft copy upload + form)
  {
    path: 'mail-workflow/new',
    component: MailInitiateComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'New Mail - SkyPlus' }
  },

  // Open a saved draft for editing / completing / dispatching
  {
    path: 'mail-workflow/draft/:draftId',
    component: MailInitiateComponent,   // same component — detect draftId to pre-fill
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'Edit Draft - SkyPlus' }
  },

  // Screen 7: Internal Memo Template Picker
  {
    path: 'mail-workflow/memo/template',
    component: MailMemoTemplateComponent,
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'Select Memo Template - SkyPlus' }
  },

  // Screen 8: OnlyOffice editor for a memo draft
  {
    path: 'mail-workflow/memo/edit/:draftId',
    component: MailWorkflowDetailComponent,  // handles OO embed when mailType=INTERNAL_MEMO + status=DRAFT
    canActivate: [AuthGuard,MenuGuard],
    data: { title: 'Edit Memo - SkyPlus' }
  },
  {
    path: 'workflow-admin',
    loadChildren: () =>
      import('./components/skyplus/wf-admin/wf-admin.routes')
        .then(m => m.WF_ADMIN_ROUTES),
    canActivate: [AuthGuard, MenuGuard],
    data: { title: 'Workflow Admin - SkyPlus' }
  },
  {
    path: 'records',
    loadChildren: () =>
      import('./components/skyplus/records/records.routes')
        .then(m => m.RECORDS_ROUTES),
    canActivate: [AuthGuard, MenuGuard],
    data: { title: 'Records Management - SkyPlus' }
  },
   {
    path: 'workflow',
    loadChildren: () =>
      import('./components/skyplus/workflow/workflow.routes')
        .then(m => m.WORKFLOW_ROUTES),
    canActivate: [AuthGuard, MenuGuard],
    data: { title: 'Workflows - SkyPlus' }
  },
    {
    path: 'documents/:id/flip',
    component: FlipViewerComponent,
    canActivate: [AuthGuard, FeatureGuard],   // <-- feature gate
    data: { feature: 'flipViewer' }            // <-- the flag name
  },
  {
    path: 'intelligence',
    component: IntelligenceComponent,
    canActivate: [FeatureGuard],
    data: { feature: 'aiChat' }
  },
  {
    path: '',
    redirectTo: '/dashboard',
   pathMatch: 'full' as const
  }
];